import { parseXmlDate2 } from '../../../utility/date.helpers';
import {
    typeDataSource,
    typeOption,
    typeXPath,
} from '../constants/parameterTypes';
import {
    getHolidayList,
    isHolidayDate,
    isWeekendDay,
} from '../helpers/holiday.helpers';

const optionalParameters = [
    {
        name: 'component',
        description:
            'Desired portion of the date difference (days | weekdays | hours | minutes | seconds |milliseconds | totaldays | totalhours | totalminutes | totalseconds | totalmilliseconds)',
        type: typeOption,
        options: [
            {
                name: 'Days',
                value: 'days',
            },
            {
                name: 'Weekdays',
                value: 'weekdays',
            },
            {
                name: 'Hours',
                value: 'hours',
            },
            {
                name: 'Minutes',
                value: 'minutes',
            },
            {
                name: 'Seconds',
                value: 'seconds',
            },
            {
                name: 'Milli Seconds',
                value: 'milliseconds',
            },
            {
                name: 'Total Days',
                value: 'totaldays',
            },
            {
                name: 'Total Hours',
                value: 'totalhours',
            },
            {
                name: 'Total Minutes',
                value: 'totalminutes',
            },
            {
                name: 'Total Seconds',
                value: 'totalseconds',
            },
            {
                name: 'Total Milliseconds',
                value: 'totalmilliseconds',
            },
        ],
    },
    {
        name: 'dsnamehol',
        description:
            'If using /component=weekdays, a secondary data source can be included for holidays to exclude. Use with /xpathhol to indicate a location for excluded holiday dates',
        type: typeDataSource,
    },
    {
        name: 'xpathhol',
        description:
            'If using /component=weekdays, /xpathhol allows you to include a repeating field or field in a repeating group with holidays that should be excluded in the date math. If the values are in your main data source, /dsnamehol is not needed',
        type: typeXPath,
        dsParentName: 'dsnamehol',
    },
];
const requiredParameters = [
    {
        name: 'date1',
        description: 'The start date',
    },
    {
        name: 'date2',
        description: 'The date to subtract from date1',
    },
];
const date1InvalidErrorMsg = 'The first date is invalid.';
const date2InvalidErrorMsg = 'The second date is invalid.';
const dateDiffErrorInvalidXpath = 'Holidays node could not be selected.';
const datediffErrorInvalidParamCombination =
    'If /xpathhol is provided, the weekdays component must be used.';
const datediffErrorComponentInvalid = 'The component is invalid.';
const description = 'Calculates the difference between two dates.';

function getWeekDays(dt1, dt2, holidays) {
    var holidayList = getHolidayList(holidays),
        weekDays = 0,
        // eslint-disable-next-line no-use-before-define
        totalDays = getDateDiffbyComponent(dt1, dt2, 'days'),
        newDate = dt1,
        direction = totalDays < 0 ? -1 : 1;

    while (totalDays !== 0) {
        newDate = new Date(
            Date.UTC(
                newDate.getUTCFullYear(),
                newDate.getUTCMonth(),
                newDate.getUTCDate() + -1 * direction,
            ),
        );

        if (!isWeekendDay(newDate) && !isHolidayDate(holidayList, newDate)) {
            weekDays += direction;
        }

        totalDays -= direction;
    }

    return weekDays;
}

function getDateDiffbyComponent(dt1, dt2, component, holidays) {
    var milliSeconds = dt1 - dt2,
        dateDiff = new Date(milliSeconds);

    switch (component.toLowerCase()) {
        case 'd':
        case 'days':
            return Math.floor(milliSeconds / (1000 * 60 * 60 * 24));

        case 'weekdays':
            //TODO: Check Holiday count.
            return getWeekDays(dt1, dt2, holidays);

        case 'h':
        case 'hours':
            return dateDiff.getUTCHours();

        case 'm':
        case 'minutes':
            return dateDiff.getUTCMinutes();

        case 's':
        case 'seconds':
            return dateDiff.getUTCSeconds();

        case 'ms':
        case 'milliseconds':
            return dateDiff.getUTCMilliseconds();

        case 'td':
        case 'totaldays':
            return milliSeconds / (1000 * 60 * 60 * 24);

        case 'th':
        case 'totalhours':
            return milliSeconds / (1000 * 60 * 60);

        case 'tm':
        case 'totalminutes':
            return milliSeconds / (1000 * 60);

        case 'ts':
        case 'totalseconds':
            return milliSeconds / 1000;

        case 'tms':
        case 'totalmilliseconds':
            return milliSeconds;

        default:
            throw new Error(datediffErrorComponentInvalid);
    }
}

function DateDiff(params) {
    function executeAsync() {
        var commonFunction = params.commonFunction,
            dataSources = params.dataSources,
            date1 = commonFunction.getParamValue('date1'),
            date2 = commonFunction.getParamValue('date2'),
            component = commonFunction.getParamValue('component', 'weekdays'),
            dsnamehol = commonFunction.getParamValue('dsnamehol'),
            xpathhol = commonFunction.getParamValue('xpathhol'),
            dt1Obj = parseXmlDate2(date1),
            dt2Obj = parseXmlDate2(date2),
            holidays = null;

        if (!dt1Obj) {
            throw new Error(date1InvalidErrorMsg);
        }

        if (!dt2Obj) {
            throw new Error(date2InvalidErrorMsg);
        }

        var dt1 = dt1Obj.date,
            dt2 = dt2Obj.date;

        if (xpathhol) {
            var dom = dataSources.getDom(dsnamehol);
            holidays = commonFunction.getValidNodeSet(
                dom,
                xpathhol,
                dateDiffErrorInvalidXpath,
            );

            if (component !== 'weekdays') {
                throw new Error(datediffErrorInvalidParamCombination);
            }
        }

        return getDateDiffbyComponent(dt1, dt2, component, holidays);
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default DateDiff;
